import {
  BlockChange,
  hasVariantOverrides,
  isBlockWithVariantOverrides,
  DateBlock,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { clsx } from 'clsx'
import { Input } from '~/sotano-kit/primitives/input'
import { DiffLine } from '../../../../../components/diff/block/line'
import * as Diff from 'diff'
import * as React from 'react'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'
import {
  HTMLInputToIsoString,
  getDateBlockPlaceholder,
  isoStringToDisplayDate,
  isoStringToHTMLInput,
} from '~/lib/date-utils'

type DateBlockDiffProps = {
  change: BlockChange
  condition: 'removed' | 'added'
}

export const DateBlockDiff = ({ change, condition }: DateBlockDiffProps) => {
  const isDeletedAndConditionIsAdded =
    condition === 'added' && change.status === 'deleted'

  const finalVersion: DateBlock = React.useMemo(() => {
    if (isDeletedAndConditionIsAdded) {
      return (
        change.conflicting?.value ? change.conflicting : change.previous
      ) as DateBlock
    }

    return (
      condition === 'removed'
        ? (change.conflicting ?? change.previous)
        : change.current
    ) as DateBlock
  }, [change, condition, isDeletedAndConditionIsAdded])

  const diffValue = React.useMemo(() => {
    const prevVersion = (change.conflicting ?? change.previous) as DateBlock

    return Diff.diffWords(
      prevVersion?.value
        ? isoStringToDisplayDate(new Date(prevVersion.value).toISOString(), {
            useTime: prevVersion?.typeOptions.useTime,
          })
        : getDateBlockPlaceholder({
            useTime: prevVersion ? prevVersion.typeOptions.useTime : false,
          }),
      finalVersion?.value
        ? isoStringToDisplayDate(new Date(finalVersion.value).toISOString(), {
            useTime: finalVersion.typeOptions.useTime,
          })
        : getDateBlockPlaceholder({
            useTime: finalVersion ? finalVersion.typeOptions.useTime : false,
          })
    )
  }, [change.conflicting, change.previous, finalVersion])

  return (
    <>
      <Input asChild>
        <div
          className={clsx(
            'relative flex',
            isDeletedAndConditionIsAdded && 'opacity-30'
          )}
        >
          <input
            type={finalVersion?.typeOptions.useTime ? 'datetime-local' : 'date'}
            value={
              finalVersion?.value
                ? isoStringToHTMLInput(
                    new Date(finalVersion.value).toISOString(),
                    { useTime: finalVersion.typeOptions.useTime }
                  )
                : ''
            }
            disabled
            className="ml-px !z-20 border-none !text-dark-bright focus:outline-none w-full placeholder:select-none disabled:!cursor-text !opacity-100 bg-transparent text-sm !h-auto"
            readOnly
          />
          <DiffLine
            diff={diffValue}
            condition={condition}
            className="absolute left-0 top-1/2 -translate-y-1/2 text-sm ml-3.5 tracking-wider text-transparent z-10"
          />
        </div>
      </Input>
      <DateOverrideDiff change={change} condition={condition} />
    </>
  )
}

type OverrideDiff = {
  overrideKey: string
  className?: string
  diffValue: Diff.Change[]
}

const DateOverrideDiff = ({
  change,
  condition,
}: Omit<DateBlockDiffProps, 'type'>) => {
  const isDeletedAndConditionIsAdded =
    condition === 'added' && change.status === 'deleted'
  const isConflictOnVariants = React.useMemo(
    () =>
      change.status === 'conflict' &&
      change.conflictPaths.includes('variantOverrides'),
    [change]
  )

  // If the block type changed (e.g: from comment to date), we don't consider the previous data for the overrides.
  const beforeDateBlock = React.useMemo(() => {
    const previous = isConflictOnVariants ? change.conflicting : change.previous
    if (previous && previous.type !== 'date') return null
    return previous
  }, [change.conflicting, change.previous, isConflictOnVariants])

  const variantsToIterate: OverrideDiff[] = React.useMemo(() => {
    if (!change.current) {
      if (
        condition === 'removed' &&
        beforeDateBlock &&
        isBlockWithVariantOverrides(beforeDateBlock)
      ) {
        return Object.entries(beforeDateBlock.variantOverrides ?? {}).map(
          ([key, override]) => {
            const placeholder = getDateBlockPlaceholder({
              useTime: beforeDateBlock.typeOptions.useTime,
            })

            const diffValue = Diff.diffChars(
              override.value
                ? HTMLInputToIsoString(override.value)
                : placeholder,
              placeholder
            )

            return { overrideKey: key, diffValue }
          }
        )
      }
      return []
    }

    invariant(
      !beforeDateBlock || isBlockWithVariantOverrides(beforeDateBlock),
      'Date Block should allow variant overrides'
    )
    if (!hasVariantOverrides(change.current)) return []
    const _overrides = Object.entries(
      change.current.variantOverrides ?? {}
    ).map(([key, override]) => {
      const previous = String(
        (beforeDateBlock?.variantOverrides &&
          beforeDateBlock.variantOverrides[key]?.value) ||
          ''
      )
      const diffValue = Diff.diffChars(
        previous
          ? HTMLInputToIsoString(previous)
          : getDateBlockPlaceholder({
              useTime: beforeDateBlock?.typeOptions.useTime ?? false,
            }),
        override.value
          ? HTMLInputToIsoString(override.value)
          : getDateBlockPlaceholder({
              useTime: override.typeOptions.useTime ?? false,
            })
      )
      return {
        overrideKey: key,
        diffValue,
      }
    })

    if (condition === 'removed') {
      for (const [key, missingOverride] of Object.entries(
        beforeDateBlock?.variantOverrides ?? {}
      )) {
        if (!_overrides.find((o) => key === o.overrideKey)) {
          const placeholder = getDateBlockPlaceholder({
            useTime: beforeDateBlock?.typeOptions.useTime ?? false,
          })

          const diffValue = Diff.diffChars(
            missingOverride.value
              ? HTMLInputToIsoString(missingOverride.value)
              : placeholder,
            placeholder
          )
          _overrides.push({
            overrideKey: key,
            diffValue,
          })
        }
      }
    }

    return _overrides
  }, [beforeDateBlock, change, condition])

  const Children = React.useCallback(
    (props: OverrideDiff) => (
      <Input
        asChild
        className={clsx(
          isDeletedAndConditionIsAdded && 'opacity-30',
          'whitespace-pre-wrap',
          props.className
        )}
      >
        <DiffLine
          diff={props.diffValue}
          condition={condition}
          className={clsx(
            isDeletedAndConditionIsAdded && 'opacity-30',
            condition === 'removed' && '!text-dark-muted',
            'py-2 !h-auto'
          )}
        />
      </Input>
    ),
    [condition, isDeletedAndConditionIsAdded]
  )

  if (!variantsToIterate.length) return null

  return (
    <OverridesDiffWrapper<OverrideDiff> overrides={variantsToIterate}>
      {Children}
    </OverridesDiffWrapper>
  )
}
